import type { EmployeeDirectoryMember, EmployeeDirectoryMembership, EmployeeDirectoryOrganization } from './model';

export type DirectoryGroupMember = EmployeeDirectoryMember & {
  groupMemberships: EmployeeDirectoryMembership[];
  additionalMembershipCount: number;
};

export type DirectoryGroup = {
  organization: EmployeeDirectoryOrganization;
  path: EmployeeDirectoryOrganization[];
  members: DirectoryGroupMember[];
  totalMemberCount: number;
  inclusiveMemberCount: number;
};

export type DirectoryIndex = {
  groups: DirectoryGroup[];
  leaderIds: ReadonlySet<string>;
  totalMemberCount: number;
};

export type DirectoryGroupSummary = {
  organizationCount: number;
  unassignedMemberCount: number;
};

export const unassignedOrganization: EmployeeDirectoryOrganization = {
  id: '__employee-directory-unassigned__',
  name: '소속 미확인',
};

export function normalizeDirectoryQuery(value: string) {
  return value.trim().toLocaleLowerCase('ko-KR');
}

export function matchesDirectoryQuery(text: string, query: string) {
  const normalizedText = normalizeDirectoryQuery(text);
  return normalizeDirectoryQuery(query).split(/\s+/).filter(Boolean).every((term) => normalizedText.includes(term));
}

/** 이전 응답의 주 소속을 유지하고, 같은 소속·직무가 반복되면 한 번만 표시합니다. */
export function employeeDirectoryMemberships(member: EmployeeDirectoryMember): EmployeeDirectoryMembership[] {
  const source = member.memberships?.length ? member.memberships : [{
    organizationId: member.organizationId, title: member.title, isPrimary: true,
  }];
  const memberships = new Map<string, EmployeeDirectoryMembership>();
  for (const membership of source) {
    const normalized = { ...membership, organizationId: membership.organizationId || undefined, title: membership.title.trim() };
    const key = JSON.stringify([normalized.organizationId, normalized.title]);
    const previous = memberships.get(key);
    memberships.set(key, { ...normalized, isPrimary: normalized.isPrimary || Boolean(previous?.isPrimary) });
  }
  return [...memberships.values()].sort((left, right) => Number(right.isPrimary) - Number(left.isPrimary));
}

export function countEmployeeDirectoryMembers(groups: readonly DirectoryGroup[]) {
  return new Set(groups.flatMap((group) => group.members.map((member) => member.id))).size;
}

function organizationPath(
  organization: EmployeeDirectoryOrganization,
  byId: ReadonlyMap<string, EmployeeDirectoryOrganization>,
) {
  const path: EmployeeDirectoryOrganization[] = [];
  const visited = new Set<string>();
  let current: EmployeeDirectoryOrganization | undefined = organization;

  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    path.unshift(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }

  return path;
}

function compareGroups(left: DirectoryGroup, right: DirectoryGroup) {
  if (left.organization.id === unassignedOrganization.id) return 1;
  if (right.organization.id === unassignedOrganization.id) return -1;
  const leftPath = left.path.map((node) => node.name).join('\u0000');
  const rightPath = right.path.map((node) => node.name).join('\u0000');
  return leftPath.localeCompare(rightPath, 'ko-KR');
}

export function buildEmployeeDirectoryIndex(
  organizations: readonly EmployeeDirectoryOrganization[],
  members: readonly EmployeeDirectoryMember[],
): DirectoryIndex {
  const byId = new Map(organizations.map((organization) => [organization.id, organization]));
  const membersByOrganization = new Map<string, DirectoryGroupMember[]>();
  const leaderIds = new Set(organizations.flatMap((organization) => (
    organization.managerId ? [organization.managerId] : []
  )));
  const uniqueMembers = new Map(members.map((member) => [member.id, member]));

  for (const member of uniqueMembers.values()) {
    const memberships = employeeDirectoryMemberships(member);
    const primaryOrganizationId = memberships.find((membership) => membership.isPrimary)?.organizationId ?? member.organizationId;
    const additionalMembershipCount = new Set(memberships
      .filter((membership) => membership.organizationId !== primaryOrganizationId)
      .map((membership) => membership.organizationId ?? unassignedOrganization.id)).size;
    const membershipsByOrganization = new Map<string, EmployeeDirectoryMembership[]>();
    for (const membership of memberships) {
      const organizationId = membership.organizationId && byId.has(membership.organizationId)
        ? membership.organizationId : unassignedOrganization.id;
      const groupMemberships = membershipsByOrganization.get(organizationId) ?? [];
      groupMemberships.push(membership);
      membershipsByOrganization.set(organizationId, groupMemberships);
    }
    for (const [organizationId, groupMemberships] of membershipsByOrganization) {
      const organizationMembers = membersByOrganization.get(organizationId) ?? [];
      organizationMembers.push({
        ...member,
        groupMemberships,
        additionalMembershipCount,
      });
      membersByOrganization.set(organizationId, organizationMembers);
    }
  }

  const sortMembers = (groupMembers: readonly DirectoryGroupMember[], managerId?: string) => [...groupMembers].sort((left, right) => {
    const leaderOrder = Number(right.id === managerId) - Number(left.id === managerId);
    return leaderOrder || left.name.localeCompare(right.name, 'ko-KR');
  });
  const groups = organizations.map((organization) => {
    const organizationMembers = sortMembers(membersByOrganization.get(organization.id) ?? [], organization.managerId);
    return {
      organization,
      path: organizationPath(organization, byId),
      members: organizationMembers,
      totalMemberCount: organizationMembers.length,
      inclusiveMemberCount: 0,
    };
  });

  const inclusiveMembers = new Map(groups.map((group) => [group.organization.id, new Set<string>()]));
  for (const group of groups) {
    for (const ancestor of group.path) {
      const ids = inclusiveMembers.get(ancestor.id);
      for (const member of group.members) ids?.add(member.id);
    }
  }
  for (const group of groups) group.inclusiveMemberCount = inclusiveMembers.get(group.organization.id)?.size ?? 0;

  const unassignedMembers = membersByOrganization.get(unassignedOrganization.id) ?? [];
  if (unassignedMembers.length > 0) {
    const sortedUnassignedMembers = sortMembers(unassignedMembers);
    groups.push({
      organization: unassignedOrganization,
      path: [unassignedOrganization],
      members: sortedUnassignedMembers,
      totalMemberCount: sortedUnassignedMembers.length,
      inclusiveMemberCount: sortedUnassignedMembers.length,
    });
  }

  return {
    groups: groups.sort(compareGroups),
    leaderIds,
    totalMemberCount: uniqueMembers.size,
  };
}

function isWithinOrganizationScope(group: DirectoryGroup, organizationId?: string, includeDescendants = true) {
  return !organizationId || (includeDescendants
    ? group.path.some((organization) => organization.id === organizationId)
    : group.organization.id === organizationId);
}

export function filterEmployeeDirectoryGroups(
  groups: readonly DirectoryGroup[],
  query: string,
  organizationId?: string,
  includeDescendants = true,
) {
  return groups.flatMap((group) => {
    if (!isWithinOrganizationScope(group, organizationId, includeDescendants)) return [];
    const pathLabel = group.path.map((organization) => organization.name).join(' ');
    const organizationMatches = matchesDirectoryQuery(pathLabel, query);
    const matchingMembers = group.members.filter((member) => (
      matchesDirectoryQuery(`${member.name} ${member.title} ${pathLabel}`, query)
    ));
    if (!organizationMatches && matchingMembers.length === 0) return [];
    return [{ ...group, members: organizationMatches ? group.members : matchingMembers }];
  });
}

export function filterEmployeeDirectoryMembers(
  groups: readonly DirectoryGroup[],
  members: readonly EmployeeDirectoryMember[],
  query: string,
  organizationId?: string,
  includeDescendants = true,
): EmployeeDirectoryMember[] {
  const matchingIds = new Set(filterEmployeeDirectoryGroups(groups, query, organizationId, includeDescendants)
    .flatMap((group) => group.members.map((member) => member.id)));
  return [...new Map(members.filter((member) => matchingIds.has(member.id)).map((member) => [member.id, member])).values()]
    .sort((left, right) => left.name.localeCompare(right.name, 'ko-KR'));
}

export function directoryOrganizationMembers(
  groups: readonly DirectoryGroup[],
  organizationId: string,
  includeDescendants = false,
): DirectoryGroupMember[] {
  const organization = groups.find((group) => group.organization.id === organizationId)?.organization;
  const uniqueMembers = new Map<string, DirectoryGroupMember>();
  for (const group of groups) {
    if (!isWithinOrganizationScope(group, organizationId, includeDescendants)) continue;
    for (const member of group.members) {
      const previous = uniqueMembers.get(member.id);
      uniqueMembers.set(member.id, previous ? {
        ...previous,
        groupMemberships: employeeDirectoryMemberships({
          ...previous, memberships: [...previous.groupMemberships, ...member.groupMemberships],
        }),
      } : member);
    }
  }
  return [...uniqueMembers.values()].sort((left, right) => (
    Number(right.id === organization?.managerId) - Number(left.id === organization?.managerId)
    || left.name.localeCompare(right.name, 'ko-KR')
  ));
}

export function summarizeEmployeeDirectoryGroups(groups: readonly DirectoryGroup[]): DirectoryGroupSummary {
  return groups.reduce<DirectoryGroupSummary>((summary, group) => {
    if (group.organization.id === unassignedOrganization.id) {
      summary.unassignedMemberCount += group.members.length;
    } else {
      summary.organizationCount += 1;
    }
    return summary;
  }, { organizationCount: 0, unassignedMemberCount: 0 });
}
